import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { getUserProfile } from "../features/userProfile/userProfileSlice";

const FlagTutorial = () => {
  const { isAuthenticated } = useKindeAuth();
  const dispatch = useDispatch();
  const driverRef = useRef(null);
  const userProfileState = useSelector((state) => state.userProfile);
  const location = useLocation();

  // Load user profile if not already loaded
  useEffect(() => {
    if (
      isAuthenticated &&
      !userProfileState.profile &&
      !userProfileState.isLoading
    ) {
      dispatch(getUserProfile());
    }
  }, [
    isAuthenticated,
    dispatch,
    userProfileState.profile,
    userProfileState.isLoading,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !userProfileState?.isLoading &&
      userProfileState?.profile?.bsa_number &&
      location.pathname === "/pba-log"
    ) {
      const hasSeenFlagTutorial = localStorage.getItem(
        "pba_flag_tutorial_seen"
      );

      if (!hasSeenFlagTutorial) {
        // Set flag BEFORE starting tutorial
        localStorage.setItem("pba_flag_tutorial_seen", "true");

        // Initialize driver.js after a short delay
        setTimeout(() => {
          const driverInstance = driver({
            showProgress: true,
            allowKeyboardControl: true,
            overlayOpacity: 0.5,
            disableActiveInteraction: false,
            steps: [
              {
                popover: {
                  title: "Flagging Entries",
                  description:
                    "Did you notice an error in a ranking entry? You can flag it for review!",
                  side: "center",
                },
              },
              {
                element: "table tbody tr",
                popover: {
                  title: "Expand a Row",
                  description:
                    "Click on any row in the rankings table to expand it and see more details.",
                  side: "bottom",
                },
                onHighlighted: () => {
                  // When this step is highlighted, add a click listener to any table row
                  const rows = document.querySelectorAll("table tbody tr");
                  rows.forEach((row) => {
                    const clickHandler = () => {
                      // Remove the click handler after it fires
                      rows.forEach((r) => {
                        r.removeEventListener("click", clickHandler);
                      });
                      // Move to next step
                      driverInstance.moveNext();
                    };
                    row.addEventListener("click", clickHandler);
                  });
                },
              },
              {
                element: '[data-tutorial="flag-button"]',
                popover: {
                  title: "Flag Button",
                  description:
                    "Once expanded, you'll see the flag button. Click it to report an issue with this entry.",
                  side: "bottom",
                },
              },
              {
                popover: {
                  title: "Help Us Maintain Accuracy",
                  description:
                    "Flagged entries are reviewed by our team to ensure the rankings are correct. Thank you for helping!",
                  side: "center",
                },
              },
            ],
          });

          driverRef.current = driverInstance;
          driverInstance.drive();
        }, 500);
      }
    }

    return () => {
      // Cleanup
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, [
    isAuthenticated,
    userProfileState?.isLoading,
    userProfileState?.profile?.bsa_number,
    location.pathname,
  ]);

  return null; // Driver.js handles its own DOM rendering
};

export default FlagTutorial;
